import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { DocumentSigningOrder } from 'src/app/modules/loan-docs/models/document-signing-order.model';
import { NotificationService } from 'src/app/services/notification.service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components';
import { ESignatureService } from 'src/app/modules/esignature/services/esignature.service';
import { ApplicationContextService } from 'src/app/services/application-context.service';
import { Subscription } from 'rxjs';
import { getEsignatureDescription, shouldRedactEnvelopeId } from './tpo-esignature-history.presentation';

@Component({
    selector: 'app-tpo-esignature-history',
    templateUrl: './tpo-esignature-history.component.html',
    styleUrls: ['./tpo-esignature-history.component.scss'],
    standalone: false
})
export class TpoEsignatureHistoryComponent extends ApplicationContextBoundComponent implements OnInit, OnDestroy {

  orders: DocumentSigningOrder[] = null;
  filteredOrders: DocumentSigningOrder[] = [];
  expandedOrderId: number = null;
  docLoadingStatuses: Map<number, boolean> = new Map<number, boolean>();
  redactEnvelopeId: boolean = false;

  private _loanId: number;
  private _loanInfoChangesSubscription: Subscription;
  private _contextSubscription: Subscription;
  private _isPRMG: boolean = false;
  private _isTpoUser: boolean = false;

  /* Pizza tracker steps for e-signature progress */
  readonly trackerSteps: string[] = ['Created', 'Sent', 'Delivered', 'Completed'];

  constructor(
    private readonly injector: Injector,
    private readonly _eSignService: ESignatureService,
    private readonly _notifService: NotificationService,
    private readonly _applicationContextService: ApplicationContextService
  ) {
    super(injector);

    this._loanId = this.applicationContext.application
      ? this.applicationContext.application.applicationId
      : null;

    this._loanInfoChangesSubscription = this.applicationContextService.loanInfoChanges.subscribe(({ context }) => {
      if (context.application) {
        this._loanId = context.application.applicationId;
      }
    });
  }

  ngOnInit() {
    this._contextSubscription = this._applicationContextService.context.subscribe(context => {
      this._isPRMG = context.isCompanyPRMG;
      this._isTpoUser = context.isTpo;
      this.redactEnvelopeId = shouldRedactEnvelopeId(
        this._isTpoUser,
        !!context.tpoConfig?.redactEnvelopeIdFromTpo
      );
      this.filterOrders();
    });
    this.getAllOrders();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this._loanInfoChangesSubscription) {
      this._loanInfoChangesSubscription.unsubscribe();
    }
    if (this._contextSubscription) {
      this._contextSubscription.unsubscribe();
    }
  }

  getAllOrders() {
    if (this._loanId) {
      this._eSignService.getSigningOrdersForApplication(this._loanId)
        .subscribe({
          next: (orders) => {
            this.orders = orders;
            this.filterOrders();
          },
          error: (err) => {
            this._notifService.showError(
              err?.message || 'Error occurred while loading e-signature orders.',
              'Error!'
            );
          }
        });
    }
  }

  toggleOrder(order: DocumentSigningOrder) {
    if (this.expandedOrderId === order.documentSigningOrderId) {
      this.expandedOrderId = null;
    } else {
      this.expandedOrderId = order.documentSigningOrderId;
    }
  }

  getDescription(order: DocumentSigningOrder): string | null {
    return getEsignatureDescription(order);
  }

  isExpanded(order: DocumentSigningOrder): boolean {
    return this.expandedOrderId === order.documentSigningOrderId;
  }

  onViewSignedDocClicked(order: DocumentSigningOrder, event: Event) {
    event.stopPropagation();
    if (!order.loanDocTasks || !order.loanDocTasks.length) {
      return;
    }
    this.docLoadingStatuses.set(order.documentSigningOrderId, true);
    const loanDocTaskId = order.loanDocTasks[0].loanDocTaskId;
    this._eSignService.downloadSignedDocument(loanDocTaskId)
      .subscribe({
        next: (data) => {
          const blob = new Blob([data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          window.open(url);
        },
        error: (err) => {
          var decodedString = String.fromCharCode.apply(null, new Uint8Array(err));
          var errorObject = JSON.parse(decodedString);
          this._notifService.showError(errorObject && errorObject.message ? errorObject.message :
            'Error occurred while viewing signed document.', "Error!");
        }
      }).add(() => {
        this.docLoadingStatuses.set(order.documentSigningOrderId, false);
      });
  }

  /**
   * Gets the current step index for the pizza tracker based on order status.
   * Steps: Created (0) -> Sent (1) -> Delivered (2) -> Completed (3)
   */
  getTrackerStepIndex(status: string): number {
    if (!status) {
      return 0;
    }
    const normalized = status.toLowerCase();
    if (normalized === 'completed' || normalized === 'signed') {
      return 3;
    } else if (normalized === 'delivered') {
      return 2;
    } else if (normalized === 'sent') {
      return 1;
    }
    // 'created', 'voided', 'declined', 'cancelled', or unknown
    return 0;
  }

  /**
   * Returns CSS class for a tracker step based on whether it's reached.
   */
  getStepClass(order: DocumentSigningOrder, stepIndex: number): string {
    const currentStep = this.getTrackerStepIndex(order.status);
    const isCancelled = this.isCancelledOrDeclined(order.status);

    if (isCancelled) {
      return stepIndex === 0 ? 'step-error' : 'step-inactive';
    }
    if (stepIndex <= currentStep) {
      return 'step-complete';
    }
    return 'step-inactive';
  }

  isCancelledOrDeclined(status: string): boolean {
    if (!status) {
      return false;
    }
    const normalized = status.toLowerCase();
    return normalized === 'voided' || normalized === 'declined' || normalized === 'cancelled';
  }

  getStatusBadgeClass(status: string): string {
    if (!status) {
      return 'badge-secondary';
    }
    const normalized = status.toLowerCase();
    if (normalized === 'completed' || normalized === 'signed') {
      return 'badge-success';
    } else if (normalized === 'sent' || normalized === 'delivered') {
      return 'badge-info';
    } else if (normalized === 'voided' || normalized === 'declined' || normalized === 'cancelled') {
      return 'badge-danger';
    }
    return 'badge-secondary';
  }

  getRecipientStatusIcon(status: string): string {
    if (!status) {
      return 'fas fa-clock';
    }
    const normalized = status.toLowerCase();
    if (normalized === 'completed' || normalized === 'signed') {
      return 'fas fa-check-circle';
    } else if (normalized === 'sent' || normalized === 'delivered') {
      return 'fas fa-paper-plane';
    } else if (normalized === 'declined') {
      return 'fas fa-times-circle';
    }
    return 'fas fa-clock';
  }

  getRecipientStatusClass(status: string): string {
    if (!status) {
      return 'status-pending';
    }
    const normalized = status.toLowerCase();
    if (normalized === 'completed' || normalized === 'signed') {
      return 'status-complete';
    } else if (normalized === 'sent' || normalized === 'delivered') {
      return 'status-in-progress';
    } else if (normalized === 'declined') {
      return 'status-error';
    }
    return 'status-pending';
  }

  private filterOrders() {
    if (!this.orders) {
      this.filteredOrders = [];
      return;
    }
    if (this._isTpoUser && this._isPRMG) {
      this.filteredOrders = this.orders.filter(o => o?.status !== 'Cancelled');
    } else {
      this.filteredOrders = this.orders;
    }
  }
}
